import React, {useEffect, useState} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';
import RequestClass from 'pages/RequestClass';
import RequestClassHistory from 'pages/RequestClassHistory';
import {Button, Card, CircularProgress, Container, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {Lock} from '@mui/icons-material';
import $ from 'jquery';
import '../pages/RequestClass.css';
import {checkInstructorScheduleConflicts, highlightInvalidTime} from './SessionScheduleChecker';

const fetchCDICourses = async (authState: any, setState: any): Promise<void> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/cdicourses`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json; charset=UTF-8',
                'Accept': 'application/json'
            }
        });
        const data = await response.json();

        setState((prevState: any) => ({
            ...prevState,
            cdiCourses: data
        }));
    } catch (error) {
        console.log(error);
    }
};

const fetchHRBPCourses = async (authState: any, setState: any): Promise<void> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/hrbpcourses`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json; charset=UTF-8',
                'Accept': 'application/json'
            }
        });
        const data = await response.json();

        setState((prevState: any) => ({
            ...prevState,
            cdiCourses: data
        }));
    } catch (error) {
        console.log(error);
    }
};

const fetchHRBPCDICourses = async (authState: any, setState: any): Promise<void> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/hrbpcdicourses`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json; charset=UTF-8',
                'Accept': 'application/json'
            }
        });
        const data = await response.json();

        setState((prevState: any) => ({
            ...prevState,
            cdiCourses: data
        }));
    } catch (error) {
        console.log(error);
    }
};

const usertypeinfo = async (authState: any): Promise<any> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/classrequest/usertypeinfo`, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            }
        });
        const data = await response.text();

        return data;
    } catch (error) {
        console.error('Error while checking class request access and user type:', error);
        return false;
    }
};

const getTitle = (message: string, t: any): string => {
    if (message === 'requestClass.editSuccess') {
        return t('requestClass.editTitle');
    }
    if (message === 'common.loading') {
        return t('common.loading');
    }
    return t('requestClass.submitTitle');
};

const getClassTypeTitle = (message: string, t: any): string => {
    if (message === 'requestClass.editError') {
        return t('requestClass.notEditedTitle');
    }
    return t('requestClass.notSubmitted');
};

const setDialogTitle = (formError: boolean, message: string, t: any): any => {
    if (formError === false) {
        return (
            <DialogTitle id='alert-dialog-title'>{getTitle(message, t)}</DialogTitle>
        );
    }
    return (
        <DialogTitle id='alert-dialog-title'>{getClassTypeTitle(message, t)}</DialogTitle>
    );
};

const itemType = ['VILT', 'WEBINAR', 'DLC'];

const getPath = (path: any, key: any): string => {
    return path ? `${path}-${key}` : key;
};

const updateSetState = (setState: any): void => {
    setState((prevState: any) => ({
        ...prevState,
        formError: true,
        message: 'requestClass.formError',
        showMessage: true
    }));
};

const validateEmptyData = (value: any, setState: any, currentPath = ''): boolean => {
    if (currentPath.includes('startTime') || currentPath.includes('endTime')) {
        const parentPath = currentPath.split('-').slice(0, -1).join('-');
        const startTime = $(`[id*=${parentPath}-startTime]`).val();
        const endTime = $(`[id*=${parentPath}-endTime]`).val();

        highlightInvalidTime(startTime, endTime, parentPath, setState);
    }
    return typeof value === 'object';
};

const checkVirtualClassLink = (currentPath: any, courseType: any): boolean => {
    return currentPath.includes('virtualClassLink') && !itemType.includes(courseType);
};

const checkCurrentPath = (currentPath: any): boolean => {
    return currentPath.includes('secondaryInstructor') || currentPath.includes('additionalComments');
};

const isValueEmpty = (value: any): boolean => {
    return value === '';
};

const isInstructorLoading = (value: any): boolean => {
    try {
        const parsedValue = JSON.parse(value);

        return parsedValue.instructorId === 'Loading...';
    } catch {
        return false; // Ignore parsing errors for non-JSON values
    }
};

const addHighlightClass = (currentPath: any): void => {
    const highlightTag = $(`[id*=${currentPath}]`);

    highlightTag.parent().addClass('highlight');
    const selectHighlightTag = $(`select[name="${currentPath}"]`);

    selectHighlightTag.next('button').addClass('highlight');
};

const highlightEmptyTag = (currentPath: any, value: any, setState: any): void => {
    if (isValueEmpty(value) || isInstructorLoading(value)) {
        addHighlightClass(currentPath);
        updateSetState(setState);
    }
};

const highlightEmptyData = (value: any, currentPath: any, courseType: any, setState: any): void => {
    if (checkCurrentPath(currentPath)) {
        return;
    }
    if (checkVirtualClassLink(currentPath, courseType)) {
        return;
    }
    highlightEmptyTag(currentPath, value, setState);
};

function convertDatesToLocaleString(responseData: any): any {
    Object.keys(responseData.classSessions).forEach((sessionKey) => {
        const session = responseData.classSessions[sessionKey];

        session.dates.startDate = new Date(session.dates.startDate).toLocaleString('sv-SE').replace(' ', 'T');
        session.dates.endDate = new Date(session.dates.endDate).toLocaleString('sv-SE').replace(' ', 'T');
        session.startTime = new Date(session.startTime).toLocaleString('sv-SE').replace(' ', 'T');
        session.endTime = new Date(session.endTime).toLocaleString('sv-SE').replace(' ', 'T');
    });
    return responseData;
}

const validateResponse = (resp: Response): void => {
    if (!resp.ok) {
        throw new Error(`HTTP Error: ${resp.status} ${resp.statusText}`);
    }
};

const getSubmitSuccessMessage = (requestedData: any): string => {
    return requestedData.hasOwnProperty('classId') && requestedData.classId ? 'requestClass.editSuccess' : 'requestClass.submitSuccess';
};

const submitClassRequest = (requestedData: any, setState: any, authState: any, t: any): void => {
    const updatedRequestData = convertDatesToLocaleString(JSON.parse(JSON.stringify(requestedData)));

    fetch(`${process.env.REACT_APP_BACKEND_URL}/classrequests`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
            'Content-Type': 'application/json',
            'idToken': `Bearer ${authState?.idToken?.idToken}`
        },
        body: JSON.stringify(updatedRequestData)
    }).then((resp) => {
        validateResponse(resp);
        const message = getSubmitSuccessMessage(requestedData);

        setState((prevState: any) => ({
            ...prevState,
            formError: false,
            message,
            showMessage: true
        }));
        return resp;
    }).catch((error) => {
        setState((prevState: any) => ({
            ...prevState,
            formError: true,
            message: 'requestClass.editError',
            showMessage: true,
            alertMessage: `${t('requestClass.editError')}: ${error.message}`
        }));
        console.error('Class request submission failed:', error);
    });
};

const setAlertMessage = (message: any): any => {
    if (message) {
        return (<Alert
            severity='error'
            style={{
                whiteSpace: 'pre-line'
            }}
            variant='filled'
        >{message}</Alert>);
    }
    return null;
};

const renderTabContent = (state: any, handleSubmitForm: any, setState: any): any => {
    if (state.requestClass) {
        return (<RequestClass
            cdiCourses={state.cdiCourses}
            classId={''}
            requestType={'New'}
            setRequestClass={setState}
            submitForm={handleSubmitForm}
            userType={state.userType}
        />);
    }
    return (<RequestClassHistory
        cdiCourses={state.cdiCourses}
        setRequestClass={setState}
        submitForm={handleSubmitForm}
        userType={state.userType}
    />);
};

const userTypeFetchers: Record<string, (authState: any, setState: any) => Promise<void>> = {
    'HRBP_AND_CDI': fetchHRBPCDICourses,
    'HRBP': fetchHRBPCourses,
    'CDI': fetchCDICourses
};

const handleUserTypeAccess = async (userType: string, authState: any, setState: any): Promise<boolean> => {
    if (!(userType in userTypeFetchers)) {
        return false;
    }

    const fetcher = userTypeFetchers[userType];

    await fetcher(authState, setState);
    return true;
};

const setFormSubmissionState = (setState: any, requestedData: any): void => {
    setState((prevState: any) => ({
        ...prevState,
        formError: false,
        message: 'common.loading',
        showMessage: true,
        requestedData,
        alertMessage: null
    }));
};

const handleDateConflicts = (setState: any, dateConflicts: string[]): void => {
    setState((prevState: any) => ({
        ...prevState,
        formError: true,
        message: 'requestClass.formError',
        showMessage: true,
        alertMessage: dateConflicts.join('\n')
    }));
};

const updateUserTypeInState = (setState: any, userType: string): void => {
    setState((prevState: any) => ({
        ...prevState,
        userType
    }));
    console.log('User Type:', userType);
};

const checkAndHighlightEmptyData = (obj: any, courseType: any, setState: any, path = ''): void => {
    for (const key of Object.keys(obj)) {
        const value = obj[key];
        const currentPath = getPath(path, key);

        if (validateEmptyData(value, setState, currentPath)) {
            checkAndHighlightEmptyData(value, courseType, setState, currentPath);
        } else {
            highlightEmptyData(value, currentPath, courseType, setState);
        }
    }
};

const buildSubmitHandler = (setState: any) =>
    (reqState: any, courseDetails: any): ((e: any) => Promise<void>) => async (e: any): Promise<void> => {
        e.preventDefault();
        $('.highlight').removeClass('highlight');

        const {
            courseTitle,
            courseId,
            revisionDate,
            courseType,
            classId
        } = courseDetails;
        const requestedData = {
            courseTitle,
            courseId,
            revisionDate,
            courseType,
            classSessions: reqState,
            ...classId && {
                classId
            } // Only include classId if it exists and is not empty
        };

        setFormSubmissionState(setState, requestedData);
        checkAndHighlightEmptyData(reqState, courseType, setState);
        const dateConflicts = checkInstructorScheduleConflicts(reqState);

        if (dateConflicts.length > 0) {
            handleDateConflicts(setState, dateConflicts);
        }
    };

export const ClassRequestFormComponent: React.FC = () => {
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
    const [state, setState] = useState({
        cdiCourses: [
            {
                courseId: '',
                courseTitle: 'Loading...',
                courseType: '',
                courseHours: '',
                courseMaxQty: '',
                revisionDate: ''
            }
        ],
        requestedData: {},
        formError: true,
        message: '',
        showMessage: false,
        alertMessage: null,
        requestClass: false,
        userType: ''
    });

    const handleSubmitForm = buildSubmitHandler(setState);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            try {
                const userType = await usertypeinfo(authState);
                // userType = 'HRBP_AND_CDI';

                updateUserTypeInState(setState, userType);
                const hasAccess = await handleUserTypeAccess(userType, authState, setState);

                setIsAuthorized(hasAccess);
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, [authState]);

    useEffect(() => {
        if (!state.formError) {
            submitClassRequest(state.requestedData, setState, authState, t);
        }
    }, [state.formError, authState, state.requestedData, t]);

    if (!isAuthorized) {
        return (
            <Container
                sx={{
                    paddingTop: '1rem',
                    width: '100%',
                    height: '100%'
                }}
            >
                <Card
                    sx={{
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <div
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '70vh',
                            justifyContent: 'center',
                            textAlign: 'center'
                        }}
                    >
                        {isAuthorized === null ?
                            <>
                                <CircularProgress data-fuel-size='large'/>
                                <p>{t('common.loading')}</p>
                            </> :
                            <>
                                <Lock
                                    style={{
                                        fontSize: 100
                                    }}
                                />
                                <h1>{t('accessDenied.title')}</h1>
                                <p>{t('accessDenied.message')}</p>
                            </>
                        }
                    </div>
                </Card>
            </Container>
        );
    }

    return (
        <>
            <Dialog
                aria-describedby='alert-dialog-description'
                aria-labelledby='alert-dialog-title'
                disableEscapeKeyDown
                onClose={(event, reason): void => {
                    if (reason !== 'backdropClick') {
                        setState((prevState) => ({
                            ...prevState,
                            showMessage: false
                        }));
                    }
                }}
                open={state.showMessage}
            >
                {setDialogTitle(state.formError, state.message, t)}
                <DialogContent>
                    <DialogContentText id='alert-dialog-description'>
                        {t(state.message)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        color='primary'
                        onClick={(): void => {
                            setState((prevState) => ({
                                ...prevState,
                                showMessage: false
                            }));
                            if (!state.formError) {
                                window.location.reload();
                            }
                        }}
                        type='submit'
                        variant='primary'
                    >
                        {'OK'}
                    </Button>
                </DialogActions>
            </Dialog>
            {renderTabContent(state, handleSubmitForm, setState)}
            {setAlertMessage(state.alertMessage)}
        </>);
};
